import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';

import css from './Sections.module.css';
import { AvatarLarge, Button, H3, H4, Icons } from '../../../components';
import { FormattedMessage } from '../../../util/reactIntl';

const ACCEPT_IMAGES = 'image/*';

const getImageIdString = imageId => {
  if (!imageId) return null;
  return typeof imageId === 'string' ? imageId : imageId.uuid || String(imageId);
};

const getBannerUrl = profileBanner => {
  if (!profileBanner) return null;
  if (typeof profileBanner === 'string') return profileBanner;

  return (
    profileBanner.url ||
    profileBanner.imageUrl ||
    profileBanner.variants?.['profile-banner-2x']?.url ||
    profileBanner.variants?.['profile-banner']?.url ||
    null
  );
};

const getUploadedBannerPayload = uploadedImage => {
  const variants = uploadedImage?.attributes?.variants || {};
  const selectedVariant =
    variants['profile-banner-2x'] ||
    variants['profile-banner'] ||
    variants['square-small2x'] ||
    variants['square-small'];

  return {
    imageId: getImageIdString(uploadedImage?.id),
    url: selectedVariant?.url || '',
    variantName: selectedVariant?.name || 'profile-banner-2x',
    recommendedSize: '1584 x 396 px',
    aspectRatio: '4:1',
  };
};

const SectionActionButton = props => {
  const { children, className, ...rest } = props;
  return (
    <Button type="button" className={classNames(css.inlineActionButton, className)} {...rest}>
      {children}
    </Button>
  );
};

const EditIconButton = props => {
  const { label, className, ...rest } = props;
  return (
    <button
      type="button"
      className={classNames(css.iconActionButton, css.editIconActionButton, className)}
      aria-label={label}
      title={label}
      {...rest}
    >
      <FiEdit2 aria-hidden="true" focusable="false" />
    </button>
  );
};

const DeleteIconButton = props => {
  const { label, className, ...rest } = props;
  return (
    <button
      type="button"
      className={classNames(css.iconActionButton, className)}
      aria-label={label}
      title={label}
      {...rest}
    >
      <FiTrash2 aria-hidden="true" focusable="false" />
    </button>
  );
};

const SectionUserInfo = props => {
  const {
    user,
    displayName,
    editable = false,
    bio,
    onImageUpload,
    onUpdateProfile,
    updateInProgress = false,
    uploadInProgress = false,
    uploadImageError,
    updateProfileError,
  } = props;

  const profile = user?.attributes?.profile || {};
  const publicData = profile.publicData || {};
  const profileBanner =
    publicData.profileBannerImage || publicData.profileBanner || publicData.bannerImage || null;
  const bannerUrl = getBannerUrl(profileBanner);
  const homePortAddress =
    publicData.homePort?.name ||
    publicData.homePort?.address ||
    publicData.location?.name ||
    publicData.location?.address;
  const location = homePortAddress || 'Unknown location';
  const createdAt = user?.attributes?.createdAt;
  const memberSince = createdAt
    ? new Date(createdAt).toLocaleString('en-US', { month: 'long', year: 'numeric' })
    : 'Unknown';

  const bannerInputRef = useRef(null);
  const avatarInputRef = useRef(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [firstName, setFirstName] = useState(profile.firstName || '');
  const [lastName, setLastName] = useState(profile.lastName || '');
  const [displayNameValue, setDisplayNameValue] = useState(displayName || '');
  const [bioValue, setBioValue] = useState(bio || '');
  const [bannerUploadInProgress, setBannerUploadInProgress] = useState(false);
  const [avatarUploadInProgress, setAvatarUploadInProgress] = useState(false);
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    if (!detailsOpen) {
      setFirstName(profile.firstName || '');
      setLastName(profile.lastName || '');
      setDisplayNameValue(displayName || '');
      setBioValue(bio || '');
    }
  }, [bio, detailsOpen, displayName, profile.firstName, profile.lastName]);

  const inProgress =
    updateInProgress || uploadInProgress || bannerUploadInProgress || avatarUploadInProgress;
  const detailsInvalid = !firstName.trim() || !lastName.trim();

  const saveDetails = async () => {
    if (!editable || !onUpdateProfile) return;

    setLocalError(null);
    try {
      await onUpdateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        displayName: displayNameValue.trim() || null,
        bio: bioValue || '',
      });
      setDetailsOpen(false);
    } catch (e) {
      setLocalError(e);
    }
  };

  const uploadBanner = async file => {
    if (!file || !onImageUpload || !onUpdateProfile) return;

    setLocalError(null);
    setBannerUploadInProgress(true);
    try {
      const uploadedImage = await onImageUpload({ id: `${file.name}_${Date.now()}`, file });
      const bannerPayload = getUploadedBannerPayload(uploadedImage);

      if (bannerPayload.imageId && bannerPayload.url) {
        // publicData.profileBannerImage is the public banner key; profileBanner is kept for compatibility.
        await onUpdateProfile({
          publicData: { profileBannerImage: bannerPayload, profileBanner: bannerPayload },
        });
      }
    } catch (e) {
      setLocalError(e);
    } finally {
      setBannerUploadInProgress(false);
    }
  };

  const uploadAvatar = async file => {
    if (!file || !onImageUpload || !onUpdateProfile) return;

    setLocalError(null);
    setAvatarUploadInProgress(true);
    try {
      const uploadedImage = await onImageUpload({ id: `${file.name}_${Date.now()}`, file });
      if (uploadedImage?.id) {
        await onUpdateProfile({ profileImageId: uploadedImage.id });
      }
    } catch (e) {
      setLocalError(e);
    } finally {
      setAvatarUploadInProgress(false);
    }
  };

  const removeBanner = async () => {
    if (!editable || !onUpdateProfile) return;

    setLocalError(null);
    try {
      await onUpdateProfile({
        publicData: { profileBanner: null, profileBannerImage: null, bannerImage: null },
      });
    } catch (e) {
      setLocalError(e);
    }
  };

  const imageError = localError || uploadImageError || updateProfileError;

  return (
    <div className={css.asideContent}>
      <div
        className={classNames(css.coverPhoto, {
          [css.coverPhotoFallback]: !bannerUrl,
        })}
      >
        {bannerUrl ? <img className={css.myCoverPhoto} src={bannerUrl} alt="" /> : null}
        {editable ? (
          <div className={css.bannerControls}>
            <span className={css.bannerHint}>
              <FormattedMessage id="ProfilePage.SectionUserInfo.bannerHint" />
            </span>
            <div className={css.bannerActionControls}>
              <input
                ref={bannerInputRef}
                className={css.hiddenFileInput}
                accept={ACCEPT_IMAGES}
                type="file"
                onChange={event => uploadBanner(event.target.files?.[0])}
                disabled={inProgress}
              />
              <EditIconButton
                label={bannerUrl ? 'Change profile banner' : 'Add profile banner'}
                className={css.bannerIconButton}
                onClick={() => bannerInputRef.current?.click()}
                disabled={inProgress}
              />
              {bannerUrl ? (
                <DeleteIconButton
                  label="Remove profile banner"
                  className={css.bannerDeleteButton}
                  onClick={removeBanner}
                  disabled={inProgress}
                />
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      <div className={css.avatarWrap}>
        <AvatarLarge className={css.avatar} user={user} disableProfileLink />
        {editable ? (
          <>
            <input
              ref={avatarInputRef}
              className={css.hiddenFileInput}
              accept={ACCEPT_IMAGES}
              type="file"
              onChange={event => uploadAvatar(event.target.files?.[0])}
              disabled={inProgress}
            />
            <EditIconButton
              label="Change profile picture"
              className={css.avatarEditButton}
              onClick={() => avatarInputRef.current?.click()}
              disabled={inProgress}
            />
          </>
        ) : null}
      </div>

      <div className={css.profileContent}>
        <div className={css.userInformation}>
          <div className={css.userInformationStart}>
            <H3>
              {displayName ? (
                <FormattedMessage id="ProfilePage.mobileHeading" values={{ name: displayName }} />
              ) : null}
            </H3>
            <div className={css.locationInfo}>
              <Icons name="location" />
              {location} • Member since {memberSince}
            </div>
          </div>
          {editable ? (
            <SectionActionButton onClick={() => setDetailsOpen(true)} disabled={inProgress}>
              <FormattedMessage id="ProfilePage.SectionUserInfo.editProfile" />
            </SectionActionButton>
          ) : null}
        </div>

        <div className={css.captainsLog}>
          <div className={css.sectionHeaderRow}>
            <H4 className={css.captainsLogTitle}>
              <Icons name="anchor" />
              <FormattedMessage id="ProfilePage.SectionUserInfo.captainsLogTitle" />
            </H4>
            {editable && !detailsOpen ? (
              <EditIconButton
                label="Edit Captain's Bio"
                onClick={() => setDetailsOpen(true)}
                disabled={inProgress}
              />
            ) : null}
          </div>

          {detailsOpen ? (
            <div className={css.inlineEditForm}>
              <div className={css.inlineEditGrid}>
                <label>
                  <span>
                    <FormattedMessage id="ProfileSettingsForm.firstNameLabel" />
                  </span>
                  <input value={firstName} onChange={event => setFirstName(event.target.value)} />
                </label>
                <label>
                  <span>
                    <FormattedMessage id="ProfileSettingsForm.lastNameLabel" />
                  </span>
                  <input value={lastName} onChange={event => setLastName(event.target.value)} />
                </label>
              </div>
              <label>
                <span>
                  <FormattedMessage id="ProfileSettingsForm.displayNameLabel" />
                </span>
                <input
                  value={displayNameValue}
                  onChange={event => setDisplayNameValue(event.target.value)}
                />
              </label>
              <label>
                <span>
                  <FormattedMessage id="ProfileSettingsForm.bioLabel" />
                </span>
                <textarea
                  rows={4}
                  value={bioValue}
                  onChange={event => setBioValue(event.target.value)}
                />
              </label>
              <div className={css.inlineEditActions}>
                <Button
                  type="button"
                  className={classNames(css.saveInlineButton, css.profileDetailsActionButton)}
                  onClick={saveDetails}
                  inProgress={updateInProgress}
                  disabled={detailsInvalid || inProgress}
                >
                  <FormattedMessage id="ProfileSettingsForm.saveChanges" />
                </Button>
                <SectionActionButton
                  className={css.profileDetailsActionButton}
                  onClick={() => setDetailsOpen(false)}
                  disabled={inProgress}
                >
                  <FormattedMessage id="ProfilePage.SectionUserInfo.cancel" />
                </SectionActionButton>
              </div>
            </div>
          ) : bio ? (
            <p className={css.bio}>{bio}</p>
          ) : (
            <p className={css.emptyState}>
              <FormattedMessage
                id={
                  editable
                    ? 'ProfilePage.SectionUserInfo.noBioEditable'
                    : 'ProfilePage.SectionUserInfo.noBio'
                }
              />
            </p>
          )}
          {imageError ? (
            <p className={css.inlineError}>
              <FormattedMessage id="ProfileSettingsForm.updateProfileFailed" />
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default SectionUserInfo;
